import { useCallback } from 'react';

/**
 * Custom hook to convert a number into its English word representation.
 *
 * - Handles numbers up to just before trillions (numbers with up to 12 digits).
 * - For numbers equal to or exceeding a trillion, it returns the number in string itself, not a word.
 * - To extend support for larger numbers (e.g., trillions), modify the "thousands" array accordingly.
 **/
export const useNumberToWords = () => {
  const numberToWords = useCallback(num => {
    if (num === 0) return 'zero';
    if (Math.abs(num).toString().length > 12) return num.toString();
    if (num < 0) return `minus ${numberToWords(Math.abs(num))}`;
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const thousands = ['', 'thousand', 'million', 'billion'];
    let word = '';
    function getWord(n) {
      if (n === 0) return '';
      if (n < 10) return `${ones[n]} `;
      if (n < 20) return `${teens[n - 10]} `;
      if (n < 100) return `${`${tens[Math.floor(n / 10)]} ${ones[n % 10]} `.trim()} `;
      return `${`${ones[Math.floor(n / 100)]} hundred ${getWord(n % 100)}`.trim()} `;
    }
    let i = 0;
    while (num > 0) {
      const chunk = num % 1000;
      if (chunk > 0) {
        word = `${getWord(chunk)}${thousands[i]} ${word}`.trim();
      }
      num = Math.floor(num / 1000);
      i++;
    }
    return word.trim();
  }, []);
  return numberToWords;
};
//# sourceMappingURL=useNumberToWords.js.map